/**
 * Auth Store
 *
 * Zustand store for authentication state:
 * - JWT token storage and refresh
 * - User info
 * - Tenant context
 * - Capability-based UI rendering
 */

import type { Environment, Capability, TenantId } from "@tatvex/types";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

import { apiRequest } from "../api/client";
import type { UserInfo, AuthState } from "../types";

// Refresh token 5 minutes before expiration
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;
// Minimum time between refresh attempts
const MIN_REFRESH_INTERVAL_MS = 60 * 1000;

/** Login parameters for atomic state update */
interface LoginParams {
  readonly token: string;
  readonly expiresAt: number;
  readonly refreshToken: string;
  readonly user: UserInfo;
  readonly tenantId: TenantId;
  readonly tenantSlug: string;
  readonly environment: Environment;
  readonly capabilities: readonly Capability[];
}

interface AuthActions {
  readonly login: (params: LoginParams) => void;
  readonly setToken: (token: string, expiresAt: number, refreshToken?: string) => void;
  readonly setUser: (user: UserInfo) => void;
  readonly setTenant: (tenantId: TenantId, tenantSlug: string) => void;
  readonly setEnvironment: (environment: Environment) => void;
  readonly setCapabilities: (capabilities: readonly Capability[]) => void;
  readonly logout: () => void;
  readonly isCapable: (capability: Capability) => boolean;
  readonly refreshTokenIfNeeded: () => Promise<boolean>;
  readonly hasCapability: (capability: Capability) => boolean;
  readonly hasAnyCapability: (capabilities: readonly Capability[]) => boolean;
  readonly hasAllCapabilities: (capabilities: readonly Capability[]) => boolean;
}

interface ExtendedAuthState extends AuthState {
  readonly refreshToken: string | undefined;
  readonly lastRefreshAttempt: number | undefined;
  readonly isRefreshing: boolean;
  readonly _hasHydrated: boolean;
}

type AuthStore = ExtendedAuthState & AuthActions;

const initialState: ExtendedAuthState = {
  isAuthenticated: false,
  user: undefined,
  tenantId: undefined,
  tenantSlug: undefined,
  environment: "dev",
  capabilities: [],
  token: undefined,
  tokenExpiresAt: undefined,
  refreshToken: undefined,
  lastRefreshAttempt: undefined,
  isRefreshing: false,
  _hasHydrated: true, // Default to true - sessionStorage is synchronous
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      login: (params: LoginParams) => {
        const newState = {
          token: params.token,
          tokenExpiresAt: params.expiresAt,
          refreshToken: params.refreshToken,
          user: params.user,
          tenantId: params.tenantId,
          tenantSlug: params.tenantSlug,
          environment: params.environment,
          capabilities: params.capabilities,
          isAuthenticated: true,
        };

        // Manually write to sessionStorage FIRST to ensure persistence
        const persistedState = {
          state: newState,
          version: 0,
        };
        sessionStorage.setItem("tatvex-auth", JSON.stringify(persistedState));

        // Then update Zustand state
        set(newState);

        // Schedule automatic refresh
        scheduleTokenRefresh(params.expiresAt);
      },

      setToken: (token: string, expiresAt: number, refreshToken?: string) => {
        set({
          token,
          tokenExpiresAt: expiresAt,
          isAuthenticated: true,
          refreshToken: refreshToken ?? get().refreshToken,
        });

        // Schedule automatic refresh
        scheduleTokenRefresh(expiresAt);
      },

      setUser: (user: UserInfo) => {
        set({ user });
      },

      setTenant: (tenantId: TenantId, tenantSlug: string) => {
        set({ tenantId, tenantSlug });
      },

      setEnvironment: (environment: Environment) => {
        set({ environment });
      },

      setCapabilities: (capabilities: readonly Capability[]) => {
        set({ capabilities });
      },

      logout: () => {
        // Clear any pending refresh
        clearTokenRefreshTimer();
        set(initialState);
        // Clear persisted storage
        sessionStorage.removeItem("tatvex-auth");
      },

      isCapable: (capability: Capability) => {
        const { capabilities } = get();
        return capabilities.includes(capability);
      },

      hasCapability: (capability: Capability) => {
        const { capabilities } = get();
        return capabilities.includes(capability);
      },

      hasAnyCapability: (capabilities: readonly Capability[]) => {
        const { capabilities: userCaps } = get();
        return capabilities.some((cap) => userCaps.includes(cap));
      },

      hasAllCapabilities: (capabilities: readonly Capability[]) => {
        const { capabilities: userCaps } = get();
        return capabilities.every((cap) => userCaps.includes(cap));
      },

      refreshTokenIfNeeded: async () => {
        const state = get();

        // Check if we need to refresh
        if (!state.token || !state.tokenExpiresAt || !state.refreshToken) {
          return false;
        }

        // Check if already refreshing
        if (state.isRefreshing) {
          return false;
        }

        // Check minimum interval between refresh attempts
        if (
          state.lastRefreshAttempt &&
          Date.now() - state.lastRefreshAttempt < MIN_REFRESH_INTERVAL_MS
        ) {
          return false;
        }

        // Check if token needs refresh (within buffer time of expiration)
        const now = Date.now();
        const expiresAt = state.tokenExpiresAt * 1000;
        const shouldRefresh = expiresAt - now <= TOKEN_REFRESH_BUFFER_MS;

        if (!shouldRefresh) {
          return true; // Token still valid, no refresh needed
        }

        set({ isRefreshing: true, lastRefreshAttempt: Date.now() });

        try {
          const response = await apiRequest<{
            token: string;
            expiresAt: number;
            refreshToken?: string;
          }>("/auth/refresh", {
            method: "POST",
            body: { refreshToken: state.refreshToken },
          });

          if (response.success) {
            const { token, expiresAt, refreshToken } = response.data;
            set({
              token,
              tokenExpiresAt: expiresAt,
              refreshToken: refreshToken ?? state.refreshToken,
              isRefreshing: false,
            });

            // Schedule next refresh
            scheduleTokenRefresh(expiresAt);

            return true;
          } else {
            // Refresh failed, logout
            console.warn("Token refresh failed:", response.error);
            set({ isRefreshing: false });
            get().logout();
            return false;
          }
        } catch (error) {
          console.error("Token refresh error:", error);
          set({ isRefreshing: false });
          return false;
        }
      },
    }),
    {
      name: "tatvex-auth",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        token: state.token,
        tokenExpiresAt: state.tokenExpiresAt,
        refreshToken: state.refreshToken,
        user: state.user,
        tenantId: state.tenantId,
        tenantSlug: state.tenantSlug,
        environment: state.environment,
        capabilities: state.capabilities,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Token refresh timer management
let refreshTimerId: number | undefined;

function clearTokenRefreshTimer(): void {
  if (refreshTimerId !== undefined) {
    window.clearTimeout(refreshTimerId);
    refreshTimerId = undefined;
  }
}

function scheduleTokenRefresh(expiresAt: number): void {
  clearTokenRefreshTimer();

  const now = Date.now();
  const expiresAtMs = expiresAt * 1000;
  const refreshTime = expiresAtMs - TOKEN_REFRESH_BUFFER_MS;
  const delay = Math.max(refreshTime - now, MIN_REFRESH_INTERVAL_MS);

  refreshTimerId = window.setTimeout(() => {
    const state = useAuthStore.getState();
    if (state.isAuthenticated && state.tokenExpiresAt === expiresAt) {
      state.refreshTokenIfNeeded();
    }
  }, delay);
}

// Check token expiration and schedule refresh on init
const initializeAuth = (): void => {
  const state = useAuthStore.getState();

  if (!state.tokenExpiresAt) {
    return;
  }

  const now = Date.now();
  const expiresAt = state.tokenExpiresAt * 1000;

  if (now >= expiresAt) {
    // Token already expired
    if (state.refreshToken) {
      // Try to refresh
      state.refreshTokenIfNeeded();
    } else {
      // No refresh token, logout
      state.logout();
    }
  } else if (expiresAt - now <= TOKEN_REFRESH_BUFFER_MS) {
    // Token about to expire, refresh now
    state.refreshTokenIfNeeded();
  } else {
    // Schedule refresh
    scheduleTokenRefresh(state.tokenExpiresAt);
  }
};

// Run on module load
initializeAuth();

// Export for testing
export { clearTokenRefreshTimer, scheduleTokenRefresh };
